import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AvatarStack, Button, MetaChip, Toast, tripColorFor, vars } from "@pack-it-up/ui-web";
import { asTripId, isOk, type CalendarDate, type CategoryId, type FractionalIndex, type ItemId, type NoteId, type ParticipantId, type TemplateId, type TodoId, type TripColor, type UserId } from "@pack-it-up/shared";
import { createShortcutRegistry, errorToMessage, type Neighbors } from "@pack-it-up/viewmodel";
import { categoryKind, checkedCount, copyName, isItemComplete, participantsOf } from "@pack-it-up/core";
import type { Assignment, Category, ParticipantRow, TodoItem } from "@pack-it-up/core";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useTripDetail, type TripStores } from "../../app/useTripDetail.js";
import { useSession } from "../../app/SessionProvider.js";
import { useStores } from "../../app/AppContext.js";
import { useMySettings } from "../../app/useMySettings.js";
import { ShareIcon, SettingsIcon, ClockChipIcon, TemplateChipIcon, PackingTabIcon, TodoTabIcon } from "../../app/icons.js";
import { ListTabs, isListTab, type ListTab } from "../ListTabs.js";
import { countdown } from "../../lib/format.js";
import { useShortcuts } from "../../lib/useShortcuts.js";
import type { ItemComposerDraft } from "./ItemComposer.js";
import { CopyDialog } from "../shared/CopyDialog.js";
import { ConfirmDeleteDialog } from "../shared/ConfirmDeleteDialog.js";
import { NotesTab } from "./NotesTab.js";
import type { ItemPatchDraft, TodoPatchDraft } from "./RowEditForm.js";
import { ShareDialogHost } from "../../features/share/ShareDialogHost.js";
import { TripSettingsDialog } from "./TripSettingsDialog.js";
import { Toolbar } from "./Toolbar.js";
import { TemplatesForTripModal } from "./TemplatesForTripModal.js";
import { TripTopbar } from "./TripTopbar.js";
import { SyncModeBar, syncCountsLabel } from "./SyncModeBar.js";
import { SyncConfirmDialog } from "./SyncConfirmDialog.js";
import { SyncOnlyInTemplateSection } from "./SyncOnlyInTemplateSection.js";
import { ClearCheckedDialog } from "./ClearCheckedDialog.js";
import { RenameTagDialog } from "./RenameTagDialog.js";
import { DeleteCategoryDialog } from "./DeleteCategoryDialog.js";
import { ProgressCard } from "./ProgressCard.js";
import { PackingList } from "./PackingList.js";
import { TodoList } from "./TodoList.js";
import { useTripSync } from "./useTripSync.js";

// How long a deferred delete (item/todo) stays undoable, and how long its toast
// stays up — the two must match, or the Undo button disappears before the delete
// it's guarding actually happens (or lingers after, looking actionable when it isn't).
const UNDO_WINDOW_MS = 6000;

const TripDetailView = ({ stores, tripId }: { stores: TripStores; tripId: string }): ReactNode => {
  const { detail } = stores;
  const navigate = useNavigate();
  const { auth } = useStores();
  const actorId = useStoreValue(auth, (s) => s.user?.id ?? null);
  const isAnonymous = useStoreValue(auth, (s) => s.user?.isAnonymous ?? false);
  const trip = useStoreValue(detail, (s) => s.trip);
  const role = useStoreValue(detail, (s) => s.role);
  const canEdit = useStoreValue(detail, (s) => s.canEdit);
  const categories = useStoreValue(detail, (s) => s.categories);
  const items = useStoreValue(detail, (s) => s.items);
  const todos = useStoreValue(detail, (s) => s.todos);
  const notes = useStoreValue(detail, (s) => s.notes);
  const itemsView = useStoreValue(detail, (s) => s.itemsView);
  const todosView = useStoreValue(detail, (s) => s.todosView);
  const notesView = useStoreValue(detail, (s) => s.notesView);
  const { copy, home, pendingTripDelete } = useSession();
  // Issue #203 — row display mode is a global account setting (Settings page), not a
  // per-trip preference: unlike stagingOptIn it never seeds onto MemberSettings.
  const { settings: mySettings } = useMySettings();
  const itemDisplay = mySettings?.itemDisplay ?? "condensed";
  // Issue #228 — "Done last" and the template-tag display are per-member (self-scoped
  // MemberSettings), so they read off the actor's own member entry, not trip.settings.
  const myMemberSettings = actorId === null ? undefined : trip?.members[actorId as ParticipantId]?.settings;
  const templateDisplay = myMemberSettings?.templateDisplay ?? "tag";
  const copyBusy = useStoreValue(copy, (s) => s.busy);
  const copyError = useStoreValue(copy, (s) => s.error);
  // Member setting `templateDisplay: "highlight"` — resolves a row's source template to
  // a color: the template's own color if set, else the same deterministic hash the
  // trip/sidebar cards fall back to.
  const allTemplates = useStoreValue(home, (s) => s.templates);
  const resolveTemplateColor = useCallback(
    (templateId: TemplateId): TripColor => allTemplates.find((t) => t.id === templateId)?.color ?? tripColorFor(templateId),
    [allTemplates],
  );
  const [shareOpen, setShareOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Issue #8 — "Save as template" and "Copy trip", both from the "⋮" menu.
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [copyTripOpen, setCopyTripOpen] = useState(false);
  // Permanent delete — same menu as Archive, guarded behind a confirm (Archive isn't).
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  // Dates popover state lives here (not inside TripTopbar) so the countdown meta-chip
  // can open the same popover that's anchored at the topbar's dates field.
  const [datesOpen, setDatesOpen] = useState(false);
  const packing = useMemo(() => detail.getState().progress(), [detail, items, trip]);
  // T27: staging is effectively on only for a user who's personally opted in —
  // `stagingEnabled()`/`stagingProgress()` read the actor's own MemberSettings.
  const stagingEnabled = useMemo(() => detail.getState().stagingEnabled(), [detail, trip]);
  const stagingProgress = useMemo(() => detail.getState().stagingProgress(), [detail, items, trip]);
  // Categories are typed per tab (kind; legacy docs without one count as packing). A
  // category also shows on a tab when it still contains that tab's rows — so legacy
  // todos filed under an untyped (= packing) category stay reachable on the To Do tab.
  const packingCategories = useMemo(
    () => categories.filter((c) => categoryKind(c) === "packing" || items.some((i) => i.categoryId === c.id)),
    [categories, items],
  );
  const todoCategories = useMemo(
    () => categories.filter((c) => categoryKind(c) === "todo" || todos.some((t) => t.categoryId === c.id)),
    [categories, todos],
  );
  // Apply each tab's toolbar view (sort/filter/completedToBottom).
  const visibleItems = useMemo(() => detail.getState().visibleItems(), [detail, items, itemsView, trip]);
  const visibleTodos = useMemo(() => detail.getState().visibleTodos(), [detail, todos, todosView, trip]);
  const visibleNotes = useMemo(() => detail.getState().visibleNotes(), [detail, notes, notesView]);
  // Issue #203 follow-up: the packing toolbar's assignee filter is single-select — when
  // narrowed to one person, expanded mode drops the per-participant pills (redundant
  // with the filter) and scopes the row checkbox to that one participant.
  const filterParticipant =
    itemsView.filterAssignees === "all" ? undefined : (itemsView.filterAssignees[0] as ParticipantId | undefined);
  // T29: the tags in use anywhere on this trip — the toolbar's filter options (own +
  // auto-tags) and the rename dialog. Derived from the rows; there is no tag registry.
  const tagPalette = useMemo(() => detail.getState().tagPalette(), [detail, items, todos, notes]);
  // The composer's one-click palette — own tags only, never a source template's
  // auto-tag (issue #180's "should not be selectable when creating/editing an item").
  const selectableTagPalette = useMemo(() => detail.getState().selectableTagPalette(), [detail, items, todos, notes]);
  // Issue #185 — the rows the share dialog plans a member removal against. A getter,
  // not a list: the dialog holds it in a ref so a row edit never rebuilds its store.
  const participantRows = useCallback(
    (): ParticipantRow[] => [
      ...items.map((item) => ({ collection: "items" as const, item })),
      ...todos.map((item) => ({ collection: "todos" as const, item })),
    ],
    [items, todos],
  );
  const { tab: tabParam } = useParams();
  const tab: ListTab = isListTab(tabParam) ? tabParam : "packing";

  // Undo toast for the clear-checked bulk ops (Doc 06 §9): auto-dismisses after a few
  // seconds; Undo re-applies the inverse from the relevant store's undo manager.
  const [toast, setToast] = useState<{ message: string; onUndo?: () => void } | null>(null);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  // Confirm dialog before destructively clearing packed items.
  const [clearCheckedOpen, setClearCheckedOpen] = useState(false);
  // Issue #181 — the tag the rename dialog is open for, or null.
  const [renameTag, setRenameTag] = useState<string | null>(null);
  // Category pending deletion (from the header's ⋮ menu) — confirmed via dialog.
  const [deleteCat, setDeleteCat] = useState<Category | null>(null);
  // Row delete, deferred behind an Undo toast (no confirm dialog): the row disappears
  // immediately, but the actual repo delete only fires once the window elapses. Only
  // one of each can be pending — arming a second flushes the first immediately, same
  // rule the template editor's deferred delete/removal already follow.
  const [pendingItemDeleteId, setPendingItemDeleteId] = useState<ItemId | null>(null);
  const itemDeleteTimer = useRef<{ timer: ReturnType<typeof setTimeout>; itemId: ItemId } | null>(null);
  const [pendingTodoDeleteId, setPendingTodoDeleteId] = useState<TodoId | null>(null);
  const todoDeleteTimer = useRef<{ timer: ReturnType<typeof setTimeout>; todoId: TodoId } | null>(null);
  useEffect(() => {
    if (toast === null) return undefined;
    const id = setTimeout(() => setToast(null), UNDO_WINDOW_MS);
    return () => clearTimeout(id);
  }, [toast]);

  // T28 sync back to a template (Doc 13) — the whole mode lives in this hook; the view
  // only drives it (enter / cancel / confirm) and renders off what it returns.
  const {
    sync,
    open: syncOpen,
    templateId: syncTemplateId,
    candidates: syncCandidateList,
    loading: syncLoading,
    applying: syncApplying,
    error: syncError,
    confirmOpen: syncConfirmOpen,
    setConfirmOpen: setSyncConfirmOpen,
    totals: syncTotals,
    listMode: syncListMode,
    onlyInTemplate: syncOnlyInTemplate,
  } = useTripSync(actorId, tab);

  // ── Keyboard shortcuts (Doc 06 §8) ───────────────────────────────────────
  // One registry per trip-detail view (a ref so registrations survive re-renders).
  // Bindings live where their state lives:
  //   escape       → cancel the open row edit (registered by PackingList/TodoList,
  //                  which own the inline-edit machines)
  //   cmd+enter    → start adding an item to the current (first) category — the
  //                  first category's AddRow registers it (rapid-add `begin`)
  //   enter / tab / shift+tab → the row edit form and composers implement these on
  //                  their own fields (the same commit/cancel code path as clicks,
  //                  Doc 12 §1.3); re-registering them globally would double-fire.
  //   space, ↑/↓  → require the row-selection model (T22); not bound here.
  //   cmd+backspace → "delete selected row" per Doc 06 §8 requires T22 row-selection,
  //                   but the existing store supports clearAll without selection, so we
  //                   bind it now as a clear-checked-items shortcut (a proxy that is
  //                   useful today and will be replaced/extended by T22's delete-row).
  const shortcutRegistry = useRef(createShortcutRegistry());
  const registry = shortcutRegistry.current;

  // Cmd/Ctrl+Backspace: clear all checked packing items (proxy for T22's delete-row).
  // Only registered when the editor can modify the list (canEdit + trip loaded).
  useEffect(() => {
    if (!canEdit) return undefined;
    return registry.register("editor", {
      "cmd+backspace": () => {
        const checked = items.filter((i) => {
          let n = 0;
          for (const v of Object.values(i.completions.byParticipant)) if (v.done) n++;
          return n > 0;
        });
        if (checked.length === 0) return;
        const n = checked.length;
        void stores.items.getState().clearAll("all");
        setToast({
          message: `Cleared ${n} item${n === 1 ? "" : "s"}`,
          onUndo: () => {
            void stores.items.getState().undo();
            setToast(null);
          },
        });
      },
    });
  }, [registry, canEdit, items, stores.items]);

  // Push dialog scope while any modal is open so editor shortcuts don't fire.
  useEffect(() => {
    const isOpen =
      shareOpen || settingsOpen || datesOpen || clearCheckedOpen || deleteCat !== null || templatesModalOpen || syncConfirmOpen;
    if (!isOpen) return undefined;
    const pop = registry.pushScope("dialog");
    return pop;
  }, [registry, shareOpen, settingsOpen, datesOpen, clearCheckedOpen, deleteCat, templatesModalOpen, syncConfirmOpen]);

  // Attach the global keydown listener.
  useShortcuts(registry);

  if (trip === null) return <div style={{ padding: vars.space.xl, color: vars.color.textMuted }}>Loading trip…</div>;

  // The signed-in user — as a trip participant, if they are one (a link-grant viewer
  // isn't in members, so `me` stays undefined and the completion UI hides itself).
  const me =
    actorId !== null && trip.members[actorId as ParticipantId]?.kind === "user" ? (actorId as UserId) : undefined;
  // Options for the packing assignee filter — every participant (users + managed).
  const assigneeOptions = Object.entries(trip.members).map(([id, m]) => ({ value: id as UserId, label: m.profile.displayName }));
  const participants = participantsOf(trip);
  const todosDone = todos.filter((t) => isItemComplete(t, participants)).length;
  // T27: advanceItemStaging collapses to the plain two-state cycle when staging reads
  // off, so this is the single toggle path for packing items regardless.
  const toggleItem = (itemId: string, target: ParticipantId, targetIsUser: boolean): void => {
    void detail.getState().advanceItemStaging(itemId, target, targetIsUser);
  };
  // Category "More" menu — Check all / Uncheck all: force-sets (not cycles) the actor's
  // own completion on every eligible row, so a category left in mixed states always ends
  // up fully checked or fully unchecked in one click.
  const setItemsCategoryChecked = (categoryId: CategoryId, done: boolean): void => {
    void detail.getState().setCategoryCompletion("items", categoryId, done);
  };
  const setTodosCategoryChecked = (categoryId: CategoryId, done: boolean): void => {
    void detail.getState().setCategoryCompletion("todos", categoryId, done);
  };
  const toggleTodo = (todoId: string, target: ParticipantId, targetIsUser: boolean): void => {
    void stores.todos.getState().toggle(todoId as TodoItem["id"], target, targetIsUser);
  };
  // Issue #203 — expanded mode's item-level "check/uncheck all participants" control.
  // T27: advances every relevant participant one tier forward, the row-level sibling of
  // toggleItem's advanceItemStaging — collapses to a plain packed<->unstaged force-cycle
  // when staging reads off (always true for to-dos, which have no staging field).
  const toggleAllItemParticipants = (itemId: string): void => {
    void detail.getState().advanceAllParticipantsStaging("items", itemId);
  };
  const toggleAllTodoParticipants = (todoId: string): void => {
    void detail.getState().advanceAllParticipantsStaging("todos", todoId);
  };
  const toggleCollapse = (categoryId: CategoryId): void => void stores.categories.getState().toggleCollapsed(categoryId);
  // `items.create`/`todos.create` don't accept `assignment` (only name/quantity/
  // description/notes/dueDate) — apply it as a follow-up `setAssignment` call when the
  // composer's draft differs from the create default ("everyone"), matching the existing
  // store APIs rather than extending viewmodel/core (CLAUDE.md rule 2/3).
  const addItem = (categoryId: CategoryId, draft: ItemComposerDraft): void => {
    void (async () => {
      const notes = draft.notes.trim();
      const result = await stores.items.getState().create({
        categoryId,
        name: draft.name,
        quantity: draft.quantity,
        ...(notes !== "" ? { description: notes } : {}),
        ...(draft.tags.length > 0 ? { tags: draft.tags } : {}),
      });
      if (isOk(result) && draft.assignment.kind !== "everyone") {
        void stores.items.getState().setAssignment(result.value, draft.assignment);
      }
    })();
  };
  const addTodo = (categoryId: CategoryId, draft: ItemComposerDraft): void => {
    void (async () => {
      const notes = draft.notes.trim();
      const result = await stores.todos.getState().create({
        categoryId,
        name: draft.name,
        ...(notes !== "" ? { description: notes } : {}),
        ...(draft.tags.length > 0 ? { tags: draft.tags } : {}),
        ...(draft.dueDate !== "" ? { dueDate: draft.dueDate as CalendarDate } : {}),
      });
      if (isOk(result) && draft.assignment.kind !== "everyone") {
        void stores.todos.getState().setAssignment(result.value, draft.assignment);
      }
    })();
  };
  // The active tab decides the new category's kind — creating on the Packing tab never
  // adds a category to the To Do tab and vice versa.
  const addCategory = (kind: "packing" | "todo") => (name: string, emoji?: string): void =>
    void stores.categories.getState().create({ name, parentId: trip.id, kind, ...(emoji !== undefined ? { emoji } : {}) });
  // Cascade delete (confirmed): remove the category's items and todos, then the
  // category itself. Both row kinds are swept so legacy mixed categories leave no
  // orphans behind.
  const deleteCategory = (cat: Category): void => {
    void (async () => {
      await Promise.all([
        ...items.filter((i) => i.categoryId === cat.id).map((i) => stores.items.getState().remove(i.id)),
        ...todos.filter((t) => t.categoryId === cat.id).map((t) => stores.todos.getState().remove(t.id)),
      ]);
      await stores.categories.getState().remove(cat.id);
    })();
  };
  const updateCategory = (categoryId: CategoryId, patch: { name?: string; emoji?: string | null }): void =>
    void stores.categories.getState().update({ categoryId, ...patch });
  const updateItem = (itemId: ItemId, patch: ItemPatchDraft): void => void stores.items.getState().update({ itemId, ...patch });
  const setAssignment = (itemId: string, assignment: Assignment): void => void stores.items.getState().setAssignment(itemId as ItemId, assignment);
  const resetItemToTemplate = (itemId: ItemId): void => void stores.items.getState().resetToImport(itemId);
  // Deferred delete: the row vanishes from the list right away (filtered out below by
  // `pendingItemDeleteId`), but the repo delete only fires once the Undo window elapses.
  const deleteItem = (itemId: ItemId): void => {
    const item = items.find((i) => i.id === itemId);
    if (item === undefined) return;
    const pending = itemDeleteTimer.current;
    if (pending !== null) {
      clearTimeout(pending.timer);
      void stores.items.getState().remove(pending.itemId);
    }
    const timer = setTimeout(() => {
      itemDeleteTimer.current = null;
      setPendingItemDeleteId(null);
      void stores.items.getState().remove(itemId);
    }, UNDO_WINDOW_MS);
    itemDeleteTimer.current = { timer, itemId };
    setPendingItemDeleteId(itemId);
    setToast({
      message: `Deleted "${item.name}"`,
      onUndo: () => {
        if (itemDeleteTimer.current?.itemId === itemId) {
          clearTimeout(itemDeleteTimer.current.timer);
          itemDeleteTimer.current = null;
        }
        setPendingItemDeleteId(null);
        setToast(null);
      },
    });
  };
  const updateTodo = (todoId: TodoId, patch: TodoPatchDraft): void => void stores.todos.getState().update({ todoId, ...patch });
  const pinDueDate = (todoId: TodoId, date: CalendarDate): void => void stores.todos.getState().pinDueDate(todoId, date);
  const setTodoAssignment = (todoId: TodoId, assignment: Assignment): void => void stores.todos.getState().setAssignment(todoId, assignment);
  const deleteTodo = (todoId: TodoId): void => {
    const todo = todos.find((t) => t.id === todoId);
    if (todo === undefined) return;
    const pending = todoDeleteTimer.current;
    if (pending !== null) {
      clearTimeout(pending.timer);
      void stores.todos.getState().remove(pending.todoId);
    }
    const timer = setTimeout(() => {
      todoDeleteTimer.current = null;
      setPendingTodoDeleteId(null);
      void stores.todos.getState().remove(todoId);
    }, UNDO_WINDOW_MS);
    todoDeleteTimer.current = { timer, todoId };
    setPendingTodoDeleteId(todoId);
    setToast({
      message: `Deleted "${todo.name}"`,
      onUndo: () => {
        if (todoDeleteTimer.current?.todoId === todoId) {
          clearTimeout(todoDeleteTimer.current.timer);
          todoDeleteTimer.current = null;
        }
        setPendingTodoDeleteId(null);
        setToast(null);
      },
    });
  };
  const setView = (patch: Partial<typeof itemsView>): void => detail.getState().setItemsView(patch);
  const setTodoView = (patch: Partial<typeof todosView>): void => detail.getState().setTodosView(patch);
  const setNoteView = (patch: Partial<typeof notesView>): void => detail.getState().setNotesView(patch);
  // Reports success back to the composer (NotesTab only clears the draft on `true`)
  // and surfaces a failure (e.g. missing index, offline) as a toast — before this, a
  // failed post silently cleared the composer as if it had worked.
  const addNote = async (body: string, tags?: string[]): Promise<boolean> => {
    const r = await stores.notes.getState().add(body, tags);
    if (!r.ok) {
      setToast({ message: errorToMessage(r.error) });
      return false;
    }
    return true;
  };
  const updateNote = (noteId: string, body: string): void => void stores.notes.getState().updateBody(noteId as NoteId, body);
  const updateNoteTags = (noteId: string, tags: string[]): void => void stores.notes.getState().updateTags(noteId as NoteId, tags);
  const reorderNote = (noteId: string, order: FractionalIndex): void =>
    void detail.getState().reorderNote(noteId as NoteId, order);
  const removeNote = (noteId: string): void => void stores.notes.getState().remove(noteId as NoteId);
  // Clear every checked packing item (scope "all"); the store keeps the inverse so Undo
  // restores the prior completions (Doc 06 §9).
  const clearChecked = (): void => {
    const n = items.filter((i) => checkedCount(i) > 0).length;
    if (n === 0) return;
    void stores.items.getState().clearAll("all");
    setToast({
      message: `Cleared ${n} item${n === 1 ? "" : "s"}`,
      onUndo: () => {
        void stores.items.getState().undo();
        setToast(null);
      },
    });
  };
  // Uncheck every completed todo; Undo re-checks them.
  const clearCompletedTodos = (): void => {
    if (todosDone === 0) return;
    void stores.todos.getState().clearCompleted();
    setToast({
      message: `Cleared ${todosDone} to-do${todosDone === 1 ? "" : "s"}`,
      onUndo: () => {
        void stores.todos.getState().undo();
        setToast(null);
      },
    });
  };

  // Issue #181 — rename a tag across items, todos and notes. A rename onto an existing
  // tag merges them, which the plan's inverse is the only record of, so the toast's
  // Undo is the guard (same shape as the clear-checked undo above).
  const applyRenameTag = (from: string, to: string): void => {
    void (async () => {
      const r = await detail.getState().renameTag(from, to);
      if (!r.ok) return;
      const n = r.value.entries.length;
      if (n === 0) return;
      // The filter was pinned to the old name; follow it to the new one.
      setView({ filterTags: itemsView.filterTags.length > 0 ? [r.value.to] : [] });
      setTodoView({ filterTags: todosView.filterTags.length > 0 ? [r.value.to] : [] });
      setToast({
        message: r.value.merges
          ? `Merged "${from}" into "${r.value.to}" on ${n} row${n === 1 ? "" : "s"}`
          : `Renamed to "${r.value.to}" on ${n} row${n === 1 ? "" : "s"}`,
        onUndo: () => {
          void detail.getState().undoTagRename();
          setView({ filterTags: itemsView.filterTags.length > 0 ? [from] : [] });
          setTodoView({ filterTags: todosView.filterTags.length > 0 ? [from] : [] });
          setToast(null);
        },
      });
    })();
  };

  // Drag-and-drop store commands (Doc 06 §7): features/dnd resolves the gesture into
  // neighbors; each of these is the single resulting write.
  const moveItem = (rowId: string, toCategoryId: CategoryId, neighbors: Neighbors): void => {
    void stores.items.getState().move(rowId as ItemId, toCategoryId, neighbors);
  };
  const moveTodo = (rowId: string, toCategoryId: CategoryId, neighbors: Neighbors): void => {
    void stores.todos.getState().move(rowId as TodoItem["id"], toCategoryId, neighbors);
  };
  const reorderCategory = (categoryId: CategoryId, order: FractionalIndex): void => {
    void stores.categories.getState().reorder(categoryId, order);
  };

  // T28 — enter sync mode for one imported template (Doc 13 §1). The store never reads
  // the trip itself; the page, which already holds the live streams, hands it a snapshot.
  const startSync = (templateId: TemplateId): void => {
    setTemplatesModalOpen(false);
    sync.getState().openSync(trip, { categories, items, todos });
    void sync.getState().selectTemplate(templateId);
  };
  const cancelSync = (): void => {
    setSyncConfirmOpen(false);
    sync.getState().cancel();
  };
  // Not optimistic (Doc 13 §8): the batch lands against a list this screen isn't
  // streaming, so success is only known when the applier reports. Failure leaves the
  // mode open with the ticks intact; the mode bar shows the error.
  const commitSync = (): void => {
    void (async () => {
      const r = await sync.getState().confirm();
      if (!r.ok) return;
      setSyncConfirmOpen(false);
      setToast({ message: `Synced to ${r.value.templateName} — ${syncCountsLabel(r.value.counts)}` });
    })();
  };

  // Kit `.people-chip` avatars + the applied-template count.
  const travellers = Object.values(trip.members).map((m) => ({ name: m.profile.displayName }));
  const templateCount = trip.importedTemplateIds.length;
  const tabCounts: Record<ListTab, number> = { packing: items.length, todos: todos.length, notes: notes.length };

  // Issue #8 — both land the user in the new list; a failure leaves the dialog open
  // with the store's error showing.
  const saveAsTemplate = async (name: string): Promise<void> => {
    const r = await copy.getState().createTemplateFromTrip(trip, name);
    if (!r.ok) return;
    setSaveAsTemplateOpen(false);
    navigate(`/template/${r.value}`);
  };

  const copyTrip = async (name: string, startDate?: string): Promise<void> => {
    const r = await copy.getState().copyTrip(trip, { name, ...(startDate !== undefined ? { startDate } : {}) });
    if (!r.ok) return;
    setCopyTripOpen(false);
    navigate(`/trip/${r.value}`);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <TripTopbar
        trip={trip}
        canEdit={canEdit}
        isOwner={role === "owner"}
        isViewer={role === "viewer"}
        onUpdateTrip={(patch) => void detail.getState().updateTrip({ tripId: trip.id, ...patch })}
        // Navigate immediately rather than waiting on the write to round-trip: the
        // local cache drops this trip from `home.trips` the instant the update is
        // issued (optimistic write), and staying on this page — which reads the trip
        // from that same list — would otherwise show a stuck "Loading trip…".
        onArchive={() => {
          navigate("/");
          void detail.getState().archiveTrip();
        }}
        onDelete={() => setConfirmDeleteOpen(true)}
        // Both write a NEW trip/template owned by the actor — impossible without an
        // account, so an anonymous session gets neither (and, with nothing else in it,
        // no "⋮" menu at all).
        {...(isAnonymous ? {} : { onSaveAsTemplate: () => setSaveAsTemplateOpen(true), onCopyTrip: () => setCopyTripOpen(true) })}
        datesOpen={datesOpen}
        onDatesOpenChange={setDatesOpen}
        actions={
          <>
            {/* An anonymous guest has nobody to share with, so it gets no "Share"
                affordance — it reaches the same dialog through the travellers pill,
                which reads as "who's on this trip" rather than an invitation to invite. */}
            {!isAnonymous && (
              <Button variant="ghost" size="sm" onClick={() => setShareOpen(true)}>
                {ShareIcon}
                Share
              </Button>
            )}
            {/* Settings stays open to a guest: the staging opt-in inside is self-scoped
                (how *this* session sees its own checkboxes), so it needs the dialog even
                though every trip-wide control in it is hidden from it. */}
            <Button variant="ghost" size="sm" aria-label="Trip settings" onClick={() => setSettingsOpen(true)}>
              {SettingsIcon}
            </Button>
          </>
        }
      />

      <header style={{ padding: `${vars.space.lg} ${vars.space.lg} 0` }}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: vars.space.md, marginBottom: vars.space.md }}>
          {countdown(trip.startDate, trip.endDate) !== null && (
            <button
              type="button"
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
              onClick={() => canEdit && setDatesOpen((o) => !o)}
              aria-label="Edit trip dates"
              disabled={!canEdit}
            >
              <MetaChip highlight clickable={canEdit} icon={ClockChipIcon}>
                {countdown(trip.startDate, trip.endDate)}
              </MetaChip>
            </button>
          )}
          {/* Traveller chip — clicking opens the Share modal (people/share management). */}
          <button
            type="button"
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
            onClick={() => setShareOpen(true)}
            aria-label="Manage trip travellers"
          >
            <MetaChip clickable peopleChip icon={<AvatarStack people={travellers} size="xs" />}>
              {travellers.length} traveller{travellers.length === 1 ? "" : "s"}
            </MetaChip>
          </button>
          {/* "N templates applied" chip — clicking opens the template management modal (kit #17) */}
          <button
            type="button"
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
            onClick={() => setTemplatesModalOpen(true)}
            aria-label="Manage templates for this trip"
          >
            <MetaChip clickable icon={TemplateChipIcon}>
              {templateCount} template{templateCount === 1 ? "" : "s"} applied
            </MetaChip>
          </button>
        </div>
        {trip.description !== undefined && (
          <p style={{ margin: `0 0 ${vars.space.md}`, fontSize: vars.font.sizeSm, color: vars.color.textMuted }}>{trip.description}</p>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: vars.space.md }}>
          <ProgressCard
            icon={PackingTabIcon}
            label="Packing"
            unit="packed"
            tone="packing"
            checked={packing.checked}
            required={packing.required}
            {...(stagingEnabled ? { secondary: stagingProgress.staged } : {})}
          />
          <ProgressCard icon={TodoTabIcon} label="To Do" unit="done" tone="todo" checked={todosDone} required={todos.length} />
        </div>
      </header>

      <ListTabs basePath={`/trip/${tripId}`} active={tab} counts={tabCounts} />
      <div style={{ padding: `${vars.space.md} ${vars.space.lg} ${vars.space.xl}` }}>
        {syncOpen && (
          <SyncModeBar
            candidates={syncCandidateList}
            templateId={syncTemplateId}
            counts={syncTotals}
            loading={syncLoading}
            applying={syncApplying}
            error={syncError}
            onSelectTemplate={(id) => void sync.getState().selectTemplate(id)}
            onCancel={cancelSync}
            onReview={() => setSyncConfirmOpen(true)}
          />
        )}
        {tab === "packing" && (
          <>
            {/* The toolbar's filters are hidden in sync mode: mirror semantics act on
                every row, so a filtered list would hide decisions being made. */}
            {syncListMode === undefined && (
            <>
            <Toolbar
              view={itemsView}
              onChange={setView}
              assignees={assigneeOptions}
              includeDueDateSort={false}
              tags={tagPalette}
              {...(canEdit ? { onRenameTag: setRenameTag } : {})}
              searchPlaceholder="Search items…"
              summary={`${items.length} item${items.length === 1 ? "" : "s"} across ${packingCategories.length} categor${packingCategories.length === 1 ? "y" : "ies"}`}
              trailing={
                canEdit ? (
                  <Button variant="danger" size="sm" onClick={() => setClearCheckedOpen(true)}>
                    Clear checked
                  </Button>
                ) : undefined
              }
            />
            <ClearCheckedDialog open={clearCheckedOpen} onClose={() => setClearCheckedOpen(false)} onConfirm={clearChecked} />
            </>
            )}
            <PackingList
              categories={packingCategories}
              // Sync mode ticks the whole list, so it must show the whole list. Either
              // way, a row mid-Undo-window stays hidden until it's either restored or
              // actually deleted.
              items={(syncListMode !== undefined ? items : visibleItems).filter((i) => i.id !== pendingItemDeleteId)}
              list={trip}
              {...(me !== undefined ? { actor: me } : {})}
              canEdit={canEdit && syncListMode === undefined}
              sort={itemsView.sort}
              registry={registry}
              stagingEnabled={stagingEnabled}
              tagPalette={selectableTagPalette}
              templateDisplay={templateDisplay}
              resolveTemplateColor={resolveTemplateColor}
              itemDisplay={itemDisplay}
              {...(syncListMode === undefined && filterParticipant !== undefined ? { filterParticipant } : {})}
              {...(syncListMode !== undefined ? { syncMode: syncListMode } : {})}
              onToggle={toggleItem}
              onToggleAllParticipants={toggleAllItemParticipants}
              onCheckAll={(id) => setItemsCategoryChecked(id, true)}
              onUncheckAll={(id) => setItemsCategoryChecked(id, false)}
              onToggleCollapse={toggleCollapse}
              onAddItem={addItem}
              onAddCategory={addCategory("packing")}
              onUpdateCategory={updateCategory}
              onDeleteCategory={setDeleteCat}
              onUpdateItem={updateItem}
              onSetAssignment={setAssignment}
              onResetToImport={resetItemToTemplate}
              onDeleteItem={deleteItem}
              onMoveItem={moveItem}
              onReorderCategory={reorderCategory}
            />
            {syncListMode !== undefined && (
              <SyncOnlyInTemplateSection
                rows={syncOnlyInTemplate}
                isChecked={syncListMode.isChecked}
                onToggle={syncListMode.toggle}
              />
            )}
          </>
        )}
        {tab === "todos" && (
          <>
            {syncListMode === undefined && (
            <Toolbar
              view={todosView}
              onChange={setTodoView}
              statusLabels={{ incomplete: "To do", complete: "Done" }}
              tags={tagPalette}
              {...(canEdit ? { onRenameTag: setRenameTag } : {})}
              searchPlaceholder="Search to-dos…"
              summary={`${todos.length} to-do${todos.length === 1 ? "" : "s"} across ${todoCategories.length} categor${todoCategories.length === 1 ? "y" : "ies"}`}
              trailing={
                canEdit ? (
                  <Button variant="danger" size="sm" onClick={clearCompletedTodos}>
                    Clear checked
                  </Button>
                ) : undefined
              }
            />
            )}
            <TodoList
              categories={todoCategories}
              todos={(syncListMode !== undefined ? todos : visibleTodos).filter((t) => t.id !== pendingTodoDeleteId)}
              list={trip}
              {...(me !== undefined ? { actor: me } : {})}
              canEdit={canEdit && syncListMode === undefined}
              sort={todosView.sort}
              registry={registry}
              tagPalette={selectableTagPalette}
              templateDisplay={templateDisplay}
              resolveTemplateColor={resolveTemplateColor}
              itemDisplay={itemDisplay}
              {...(syncListMode !== undefined ? { syncMode: syncListMode } : {})}
              onToggle={toggleTodo}
              onToggleAllParticipants={toggleAllTodoParticipants}
              onCheckAll={(id) => setTodosCategoryChecked(id, true)}
              onUncheckAll={(id) => setTodosCategoryChecked(id, false)}
              onToggleCollapse={toggleCollapse}
              onAddTodo={addTodo}
              onAddCategory={addCategory("todo")}
              onUpdateCategory={updateCategory}
              onDeleteCategory={setDeleteCat}
              onUpdateTodo={updateTodo}
              onPinDueDate={pinDueDate}
              onSetAssignment={setTodoAssignment}
              onDeleteTodo={deleteTodo}
              onMoveTodo={moveTodo}
              onReorderCategory={reorderCategory}
            />
            {syncListMode !== undefined && (
              <SyncOnlyInTemplateSection
                rows={syncOnlyInTemplate}
                isChecked={syncListMode.isChecked}
                onToggle={syncListMode.toggle}
              />
            )}
          </>
        )}
        {tab === "notes" && (
          <NotesTab
            notes={visibleNotes}
            canEdit={canEdit}
            currentUserId={me}
            members={trip.members}
            view={notesView}
            onViewChange={setNoteView}
            onAdd={addNote}
            onUpdate={updateNote}
            onUpdateTags={updateNoteTags}
            onReorder={reorderNote}
            tags={tagPalette}
            tagPalette={selectableTagPalette}
            onRemove={removeNote}
          />
        )}
      </div>

      {deleteCat !== null && (
        <DeleteCategoryDialog
          category={deleteCat}
          count={
            items.filter((i) => i.categoryId === deleteCat.id).length +
            todos.filter((t) => t.categoryId === deleteCat.id).length
          }
          onClose={() => setDeleteCat(null)}
          onConfirm={() => deleteCategory(deleteCat)}
        />
      )}

      {/* Issue #181 — keyed so the field starts from the tag being renamed. */}
      <RenameTagDialog
        key={renameTag ?? "none"}
        tag={renameTag}
        palette={tagPalette}
        onClose={() => setRenameTag(null)}
        onRename={applyRenameTag}
      />

      {toast !== null && (
        <div style={{ position: "fixed", bottom: vars.space.lg, left: "50%", transform: "translateX(-50%)", zIndex: 100 }}>
          <Toast message={toast.message} {...(toast.onUndo !== undefined ? { onUndo: toast.onUndo } : {})} />
        </div>
      )}

      {/* Issue #8 — the trip's structure as a new template; to-dos travel as offsets. */}
      <CopyDialog
        open={saveAsTemplateOpen}
        title="Save as template"
        subtitle="Copies this trip's categories, items and to-dos into a reusable template."
        label="Template name"
        defaultName={trip.name}
        confirmLabel="Save as template"
        busy={copyBusy}
        error={copyError}
        onClose={() => setSaveAsTemplateOpen(false)}
        onConfirm={(name) => void saveAsTemplate(name)}
      />
      {/* Issue #8 — structure only: completions, staging and travellers start fresh. */}
      <CopyDialog
        open={copyTripOpen}
        title="Copy trip"
        subtitle="Copies the lists, not the progress — nothing starts off packed, and you're the only traveller."
        label="Trip name"
        defaultName={copyName(trip.name)}
        confirmLabel="Copy trip"
        startDateLabel="Start date (optional)"
        busy={copyBusy}
        error={copyError}
        onClose={() => setCopyTripOpen(false)}
        onConfirm={(name, startDate) => void copyTrip(name, startDate)}
      />

      {/* Permanent delete — same confirm shared with My Trips' archived-row delete.
          Confirm arms the session-scoped Undo (pendingTripDelete, shown by AppShell —
          this page is gone before the window elapses) and navigates immediately, same
          reasoning as Archive above. */}
      <ConfirmDeleteDialog
        name={confirmDeleteOpen ? trip.name : null}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={() => {
          setConfirmDeleteOpen(false);
          pendingTripDelete.getState().arm(trip.id, trip.name);
          navigate("/");
        }}
      />

      {/* Unified share dialog (Doc 08 §8) — same component serves templates. Also opened
          by viewers (the traveller chip), so stays mounted for every role; `canManage`
          tells the store whether to subscribe to the owner/editor-only shareLinks and
          pendingInvites streams. */}
      <ShareDialogHost
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        resource={{ kind: "trip", id: trip.id }}
        resourceName={trip.name}
        members={trip.members}
        // A guest edits with the rest of them, but has no friends list, no email invite
        // to send, and no business minting links — so for an anonymous session the
        // dialog is purely "who's on this trip" plus its own name.
        canManage={canEdit && !isAnonymous}
        // Issue #185 — removing a member deletes the rows they were the sole allocatee of.
        rows={participantRows}
      />
      {/* Trip settings (T27) — self-service staging opt-in (any role) plus the
          trip-wide packing-together toggle (owners/editors). */}
      <TripSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        stagingOptIn={trip.members[actorId as ParticipantId]?.settings.stagingOptIn ?? false}
        onChangeStagingOptIn={(value) => void detail.getState().setMyStagingOptIn(value)}
        proxyCompletion={{
          value: trip.settings.allowProxyCompletion,
          onChange: (value) => void detail.getState().updateTrip({ tripId: trip.id, settings: { allowProxyCompletion: value } }),
          canEdit,
        }}
        tripColor={{
          value: trip.color,
          onChange: (color) => void detail.getState().updateTrip({ tripId: trip.id, color }),
          canEdit,
        }}
        completedToBottom={{
          value: myMemberSettings?.completedToBottom ?? false,
          onChange: (value) => void detail.getState().setMyCompletedToBottom(value),
        }}
        templateDisplay={{
          value: templateDisplay,
          onChange: (value) => void detail.getState().setMyTemplateDisplay(value),
        }}
        showTripWide={!isAnonymous}
      />
      {/* Templates-for-this-trip modal (kit #17 §2) */}
      <TemplatesForTripModal
        open={templatesModalOpen}
        onClose={() => setTemplatesModalOpen(false)}
        trip={trip}
        detail={detail}
        onSyncTemplate={startSync}
      />
      {/* T28 §4.6 — the only guard on a sync (Doc 13 §8): no undo. */}
      {syncConfirmOpen && syncTemplateId !== null && (
        <SyncConfirmDialog
          templateName={syncCandidateList.find((t) => t.id === syncTemplateId)?.name ?? "template"}
          counts={syncTotals}
          applying={syncApplying}
          onConfirm={commitSync}
          onClose={() => setSyncConfirmOpen(false)}
        />
      )}
    </div>
  );
};

// Trip detail / editor (Doc 05 §3–5). T16a = read-only; T16b adds the header edit +
// completion controls + todo completion. Rapid-add, category collapse/add, row rename,
// richer field editors, toolbar and notes editing land in the next slice.
export const TripDetailPage = (): ReactNode => {
  const { id } = useParams();
  const stores = useTripDetail(asTripId(id ?? ""));
  if (stores === null || id === undefined) return <div style={{ padding: vars.space.xl, color: vars.color.textMuted }}>Loading…</div>;
  return <TripDetailView stores={stores} tripId={id} />;
};
